import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const ProblemSection = () => {
  const [visibleProblems, setVisibleProblems] = useState([]);

  const problems = [
    {
      id: 1,
      icon: "Clock",
      title: "ساعات عمل محدودة",
      description: "الصيدليات مغلقة عندما تحتاجها أكثر",
      image: "https://images.unsplash.com/photo-1563512953878-dfbf129c0b87",
      imageAlt:
        "Closed pharmacy storefront with locked doors during evening hours",
      stat: "70% من الحالات الطارئة تحدث خارج ساعات العمل",
    },
    {
      id: 2,
      icon: "Users",
      title: "طوابير طويلة ومزدحمة",
      description: "انتظار مرهق وضياع للوقت الثمين",
      image: "https://images.unsplash.com/photo-1642372823694-bcee42ee7296",
      imageAlt:
        "Long queue of people waiting in crowded pharmacy with frustrated expressions",
      stat: "متوسط الانتظار 25 دقيقة في الصيدليات التقليدية",
    },
    {
      id: 3,
      icon: "AlertTriangle",
      title: "منتجات مقلدة ومشكوك فيها",
      description: "صعوبة التأكد من أصالة المنتجات",
      image: "https://images.unsplash.com/photo-1616526629549-353331fea648",
      imageAlt:
        "Comparison between authentic and counterfeit medicine packages showing quality differences",
      stat: "30% من المنتجات في السوق غير أصلية",
    },
    {
      id: 4,
      icon: "HelpCircle",
      title: "نقص الاستشارة المهنية",
      description: "عدم توفر صيادلة مؤهلين للإرشاد",
      image: "https://images.unsplash.com/photo-1576091358783-a212ec293ff3",
      imageAlt:
        "Confused customer holding medicine bottle without professional guidance in pharmacy",
      stat: "60% من العملاء يحتاجون استشارة مهنية",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const problemId = parseInt(entry.target.dataset.problemId);
            setVisibleProblems((prev) => [...new Set([...prev, problemId])]);
          }
        });
      },
      { threshold: 0.3 }
    );

    const problemElements = document.querySelectorAll("[data-problem-id]");
    problemElements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-muted">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
            <Icon name="AlertCircle" size={20} />
            <span className="font-cairo font-semibold">المشاكل الشائعة</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-noto-arabic font-bold text-text-primary mb-6">
            لماذا تعاني من هذه المشاكل؟
          </h2>

          <p className="text-lg text-text-secondary font-cairo max-w-3xl mx-auto leading-relaxed">
            نحن نفهم التحديات التي تواجهها في الحصول على الرعاية الصحية
            والمنتجات التي تحتاجها. دعنا نوضح لك كيف يمكننا حل هذه المشاكل
            نهائياً.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {problems?.map((problem, index) => (
            <div
              key={problem?.id}
              data-problem-id={problem?.id}
              className={`group transition-all duration-700 ease-out ${
                visibleProblems?.includes(problem?.id)
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="bg-white rounded-2xl shadow-card hover:shadow-lg transition-all duration-300 overflow-hidden border border-border group-hover:border-destructive/20">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={problem?.image}
                    alt={problem?.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                  {/* Problem Icon */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-destructive/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Icon name={problem?.icon} size={24} color="white" />
                  </div>

                  {/* Stat Badge */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-sm font-cairo font-semibold text-text-primary text-center">
                      {problem?.stat}
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 text-right">
                  <h3 className="text-xl font-noto-arabic font-bold text-text-primary mb-3 group-hover:text-destructive transition-colors">
                    {problem?.title}
                  </h3>

                  <p className="text-text-secondary font-cairo leading-relaxed mb-4">
                    {problem?.description}
                  </p>

                  {/* Pain Point Indicator */}
                  <div className="flex items-center justify-end gap-2 text-destructive">
                    <span className="text-sm font-cairo font-medium">
                      مشكلة شائعة
                    </span>
                    <div className="flex gap-1">
                      {[1, 2, 3]?.map((dot) => (
                        <div
                          key={dot}
                          className="w-2 h-2 bg-destructive rounded-full animate-pulse"
                          style={{ animationDelay: `${dot * 200}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-destructive/5 via-destructive/10 to-destructive/5 rounded-2xl p-8 border border-destructive/20">
            <h3 className="text-2xl font-noto-arabic font-bold text-text-primary mb-4">
              هل تعبت من هذه المشاكل؟
            </h3>
            <p className="text-text-secondary font-cairo mb-6 max-w-2xl mx-auto">
              لا تقلق، لدينا الحل المثالي لجميع هذه التحديات. اكتشف كيف يمكن
              لروما أن تغير تجربتك تماماً.
            </p>

            {/*  <div className="flex items-center justify-center gap-2 text-primary">
              <span className="font-cairo font-semibold">تابع القراءة لتعرف الحل</span>
              <Icon name="ArrowDown" size={20} className="animate-bounce" />
            </div>*/}
          </div>
        </div>

        {/* DevOra Signature */}
        <div className="text-center mt-8">
          <p className="text-sm text-text-secondary font-cairo">
            نحول الأحلام لواقع تقني، نطور البرمجيات بإتقان، وثقة، DevOra ❤️
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
